import { ReactNode } from 'react';
import Box from '@mui/material/Box';

export type TShadcnTabItem = {
  value: string;
  label: string;
  count?: number;
  disabled?: boolean;
};

type ShadcnTabsProps = {
  value: string;
  onChange: (value: string) => void;
  tabs: TShadcnTabItem[];
  size?: 'sm' | 'md';
  fullWidth?: boolean;
};

export const ShadcnTabs = ({
  value,
  onChange,
  tabs,
  size = 'md',
  fullWidth = false,
}: ShadcnTabsProps) => {
  const padY = size === 'sm' ? '4px' : '6px';
  const padX = size === 'sm' ? '10px' : '14px';
  const fontSize = size === 'sm' ? 12 : 13;

  return (
    <Box
      role="tablist"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        backgroundColor: '#f1f5f9',
        borderRadius: '8px',
        padding: '4px',
        gap: '2px',
        width: fullWidth ? '100%' : 'auto',
      }}
    >
      {tabs.map((tab) => {
        const isActive = tab.value === value;
        return (
          <Box
            key={tab.value}
            role="tab"
            aria-selected={isActive}
            onClick={() => !tab.disabled && onChange(tab.value)}
            sx={{
              flex: fullWidth ? 1 : 'initial',
              textAlign: 'center',
              cursor: tab.disabled ? 'not-allowed' : 'pointer',
              padding: `${padY} ${padX}`,
              borderRadius: '6px',
              fontFamily: 'GilroySemiBold',
              fontSize,
              color: isActive ? '#0f172a' : tab.disabled ? '#cbd5e1' : '#64748b',
              backgroundColor: isActive ? '#ffffff' : 'transparent',
              boxShadow: isActive ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none',
              transition: 'all 0.15s ease',
              userSelect: 'none',
              whiteSpace: 'nowrap',
              opacity: tab.disabled ? 0.5 : 1,
              '&:hover': {
                color: tab.disabled ? undefined : isActive ? '#0f172a' : '#0f172a',
              },
            }}
          >
            {tab.label}
            {typeof tab.count === 'number' && (
              <Box
                component="span"
                sx={{
                  ml: 0.75,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 5px',
                  borderRadius: '999px',
                  backgroundColor: isActive ? '#0f172a' : '#e2e8f0',
                  color: isActive ? '#ffffff' : '#475569',
                  fontSize: 10,
                  fontFamily: 'GilroyBold',
                  lineHeight: 1,
                }}
              >
                {tab.count}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

type ShadcnTabPanelProps = {
  value: string;
  current: string;
  children: ReactNode;
};

export const ShadcnTabPanel = ({ value, current, children }: ShadcnTabPanelProps) => {
  if (value !== current) return null;
  return <Box role="tabpanel">{children}</Box>;
};

export default ShadcnTabs;
